import { yupResolver } from "@hookform/resolvers/yup";
import { Button } from "@itandi/itandi-bb-ui/Button";
import { Flex } from "@itandi/itandi-bb-ui/Flex";
import { FormControl } from "@itandi/itandi-bb-ui/FormControl";
import { FormControlText } from "@itandi/itandi-bb-ui/FormControlText";
import { FormGroup } from "@itandi/itandi-bb-ui/FormGroup";
import { InputText } from "@itandi/itandi-bb-ui/InputText";
import { ModalBody } from "@itandi/itandi-bb-ui/ModalBody";
import { ModalFooter } from "@itandi/itandi-bb-ui/ModalFooter";
import { ModalHeader } from "@itandi/itandi-bb-ui/ModalHeader";
import { Modalv2 } from "@itandi/itandi-bb-ui/Modalv2";
import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import type React from "react";
import { useForm } from "react-hook-form";
import { useSWRConfig } from "swr";
import { object, string } from "yup";
import type { Deal } from "~/models/Deal";
import { isSuccess } from "~/utils/api/api_handler";
import { updateNote } from "~/utils/api/vendors/deals/DealEstimationHeader";
import { generatePath } from "~/utils/generatePath";
import { removeNewlines } from "~/utils/StringUtil";

type Props = {
	deal: Deal;
	defaultNote: string;
	onClose: () => void;
};

export const NoteEditModal: React.FC<Props> = ({
	deal,
	onClose,
	defaultNote
}) => {
	const { notify } = useNotification();
	const { mutate: globalMutate } = useSWRConfig();
	const {
		trigger,
		handleSubmit,
		watch,
		setValue,
		formState: { isSubmitting, isDirty }
	} = useForm<{ note: string }>({
		defaultValues: { note: defaultNote },
		resolver: yupResolver(object().shape({ note: string().max(400) }))
	});
	const noteLength = removeNewlines(watch("note")).length;
	const isInvalid = noteLength > 400;

	const onSubmit = async (): Promise<void> => {
		const result = await updateNote(deal.id, removeNewlines(watch("note")));
		if (isSuccess(result)) {
			notify({ content: "備考を保存しました。", variant: "success" });
			globalMutate(
				generatePath("/vendors/deals/:id/deal_estimation_histories", {
					id: deal.id
				})
			);
			onClose();
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};

	return (
		<Modalv2 isOpen onClose={onClose}>
			<ModalHeader title="備考の編集" />
			<form noValidate onSubmit={handleSubmit(onSubmit)}>
				<ModalBody>
					<FormGroup variant="vertical">
						<FormControl isInvalid={isInvalid} label="備考">
							<InputText
								onBlur={(e) => {
									e.preventDefault();
									trigger("note");
									setValue("note", removeNewlines(e.target.value), {
										shouldDirty: true
									});
								}}
								onChange={(e) => {
									setValue("note", e.target.value, { shouldDirty: true });
								}}
								resize="autoRows"
								rows={8}
								value={watch("note")}
							/>
							<Flex justifyContent="space-between">
								<FormControlText
									error={isInvalid ? `${noteLength}/400文字` : undefined}
									help={[
										`${noteLength}/400文字`,
										"見積書の枠内に収めるため、改行は削除されます"
									]}
								/>
							</Flex>
						</FormControl>
					</FormGroup>
				</ModalBody>
				<ModalFooter
					leftArea={
						<Button
							disabled={isSubmitting}
							onClick={onClose}
							variant="secondary"
						>
							キャンセル
						</Button>
					}
					rightArea={
						<Button
							disabled={!isDirty}
							loading={isSubmitting}
							type="submit"
							variant="primary"
						>
							保存
						</Button>
					}
				/>
			</form>
		</Modalv2>
	);
};
